// Product + marketing analytics via PostHog, gated behind cookie consent.
//
// Why PostHog (and not Umami): Umami is domain-level and cannot tell a logged-in
// portal user apart from an anonymous marketing visitor on the same domain.
// PostHog's identify() draws that line cleanly — anonymous persons are website
// visitors; identified persons are portal users — and lets us capture in-app
// feature usage (e.g. who wants the mail add-on).
//
// Host-agnostic: api_host comes from VITE_POSTHOG_HOST (default EU cloud). The
// same code runs against PostHog EU Cloud or a self-hosted instance — only the
// env value changes.
//
// Consent contract: nothing here talks to PostHog until the visitor has granted
// the "analytics" category. init() is idempotent and re-evaluates consent on
// every call and on every consent-change event.

import posthog from 'posthog-js'
import { isSelfHosted } from '../config/edition'
import { hasConsent, onConsentChange } from './consent'

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://eu.i.posthog.com'

// Analytics is a SaaS concern only. The self-hosted edition is a partner's own
// product — it must never phone home to our PostHog project.
const ENABLED = !isSelfHosted && !!KEY

let initialized = false

/** True once PostHog is booted AND analytics consent currently holds. */
function active(): boolean {
  return initialized && hasConsent('analytics')
}

/**
 * Boot analytics if (and only if) it's enabled and the visitor has consented.
 * Safe to call repeatedly and from anywhere — it self-gates. Call once at app
 * boot and re-runs automatically whenever consent changes.
 */
export function initAnalytics(): void {
  if (!ENABLED) return

  if (!hasConsent('analytics')) {
    // Consent absent or withdrawn. If we had booted earlier, stop capturing and
    // drop any stored ids so nothing lingers.
    if (initialized) {
      posthog.opt_out_capturing()
      posthog.reset()
    }
    return
  }

  if (!initialized) {
    posthog.init(KEY as string, {
      api_host: HOST,
      // Only build person profiles for identified (portal) users. Anonymous
      // marketing visitors are counted but not profiled — cheaper and privacy-lean.
      person_profiles: 'identified_only',
      // We drive $pageview manually from the router so we can tag each view
      // with the marketing-vs-app surface (see capturePageview).
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: true,
      // Consent already covers cookies; this keeps ids in a cookie so attribution
      // survives across subdomains/sessions like a normal analytics setup.
      persistence: 'localStorage+cookie',
      // Belt-and-suspenders: we only reach here with consent, but keep GA/DNT honored.
      respect_dnt: true,
    })
    initialized = true
  } else {
    posthog.opt_in_capturing()
  }
}

// React to consent changes for the rest of the app's life.
if (typeof window !== 'undefined' && ENABLED) {
  onConsentChange(initAnalytics)
}

// --- Identity: the visitor ↔ portal-user split ------------------------------

export interface AnalyticsUser {
  id: string
  email?: string
  role?: string
  plan?: string
}

/**
 * Mark the current person as an identified portal user. Call right after a
 * successful login (and once on app boot if a session is already active).
 * This is what separates "someone using the product" from "someone on the
 * website" in every PostHog report.
 */
export function identifyUser(user: AnalyticsUser): void {
  if (!active() || !user?.id) return
  posthog.identify(user.id, {
    email: user.email,
    role: user.role,
    plan: user.plan,
  })
  // Super property stamped on every subsequent event for easy surface filtering.
  posthog.register({ surface: 'app' })
}

/** Drop identity on logout so the next person on the device starts anonymous. */
export function resetAnalytics(): void {
  if (!initialized) return
  posthog.reset()
}

// --- Events -----------------------------------------------------------------

/**
 * Capture a product/marketing event. No-op unless analytics is booted and
 * consented, so call sites don't need to guard. Use snake_case event names.
 */
export function capture(event: string, properties?: Record<string, unknown>): void {
  if (!active()) return
  posthog.capture(event, properties)
}

/**
 * Manual SPA pageview. `surface` distinguishes marketing pages from in-app
 * pages regardless of auth, so acquisition and product usage never blur together.
 */
export function capturePageview(surface: 'marketing' | 'app'): void {
  if (!active()) return
  posthog.capture('$pageview', { surface })
}
